import { FormArray } from "react-reactive-form";

// Keys in a page's `formArraysKeys` are rendered by <QuestionsAnswersArray>,
// which swaps the control for a FormArray on mount. A key can therefore have
// been saved before it became an array question and come back from the API as
// a bare scalar. Normalise the saved value so restoring an older study can
// never blow up on a shape change.
export const toRestorableArray = (value, arrayType) => {
  const values = Array.isArray(value)
    ? value
    : value === null || value === undefined || value === ""
      ? []
      : [value];

  if (arrayType !== "object") return values;

  // FormBuilder.group() needs an object per row.
  return values.map(item =>
    item && typeof item === "object" ? item : { valueHelper: item ?? "" }
  );
};

// Counterpart of the above for the control side: the saved value is an array
// but the control may still be the plain one from QuestionsProvider if this
// restore runs before <QuestionsAnswersArray> mounts. Swap it the same way
// that component does, so the saved rows survive whichever order they run in.
export const getRestorableFormArray = (wizardForm, key) => {
  if (!wizardForm?.contains(key)) return null;

  const control = wizardForm.controls[key];
  if (control instanceof FormArray) return control;

  const formArray = new FormArray([]);
  wizardForm.setControl(key, formArray);

  return formArray;
};

// A page's `formArraysKeys` map is hand-maintained and only lists the array
// questions that page renders, but `wizardForm` is shared across the whole
// wizard and holds a FormArray for *every* page's array questions. Each page
// also loads more sections than its own (Efficacy and Safety both pull in
// study_design), so a key can arrive that is a FormArray on the form but absent
// from this page's map — qSingleArmMultipleColumns is the one that bit us. Such
// a key used to fall through to wizardForm.patchValue(), and FormArray's
// patchValue calls value.forEach(), which dies on a scalar.
//
// So the live control, not the map, decides whether a key restores as an array.
// Returns the row type ("object" for FormBuilder.group rows, anything else for
// plain controls) or null when the key is not an array at all.
export const getRestoreArrayType = (wizardForm, key, formArraysKeys, value) => {
  const declared = formArraysKeys?.[key];
  if (declared) return declared;

  if (!(wizardForm?.controls?.[key] instanceof FormArray)) return null;

  // Undeclared by this page, so infer the row shape from the saved value.
  const sample = Array.isArray(value)
    ? value.find(item => item !== null && item !== undefined)
    : value;

  return sample && typeof sample === "object" ? "object" : "control";
};
